import { useContext, useEffect, useReducer, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import {
  Button,
  Card,
  CardBody,
  CardSubtitle,
  CardTitle,
  Input,
} from 'reactstrap';
import styled from 'styled-components';
import ConfirmationModal from '../../../basics/ConfirmationModal';
import { MapContext } from '../../../../contexts/MapContext';
import NoteEditorToolbar from './NoteEditorToolbar';
import NoteSaveConfirmation from '../../NoteSaveConfirmation';
import LinkifiedTextField from '../../LinkifiedTextField';
import { mobileMediaQuery } from '../../../../utils/responsive';
import { MobilePanelContext } from '../../../mobile/MobilePanel';
import { captureEvent } from '../../../../utils/analytics';

const resizeTextarea = (elem) => {
  if (!elem) return;

  // Switch to auto height so we can get the full scroll height
  elem.style.height = 'auto'; // eslint-disable-line no-param-reassign

  // Make the element have the full height of its scroll height
  elem.style.height = `${elem.scrollHeight}px`; // eslint-disable-line no-param-reassign
};

const EditorContainer = styled(Card)`
  position: absolute;
  z-index: 3; // goes above mapbox info container when editor is open!
  bottom: 0;
  right: calc(var(--side-panel-width) + var(--content-x-padding));
  width: max(25vw, 304px);
  border: 1px solid var(--border-color);
  border-radius: 8px 8px 0px 0px;
  background-color: var(--background-color);

  @media ${mobileMediaQuery} {
    right: 0;
    left: 0;
    width: 100%;
  }
`;

const EditorHeader = styled.div`
  border-bottom: 1px solid var(--border-color);
  padding: var(--popup-y-padding) var(--popup-x-padding) 0px;
`;

const EditorTitle = styled(CardTitle)`
  font-weight: bold;
  font-size: 18px;
  margin: calc(var(--popup-y-padding) - 0px) auto
    calc(var(--popup-y-padding) - 6px);
  ${(props) =>
    !props.$viewmode &&
    `margin: -1.5px calc(var(--popup-x-padding) * -1) 0px;`};
`;

const EditorTitleInput = styled(Input)`
  padding: calc(var(--popup-y-padding) + 4px) var(--popup-x-padding)
    calc(var(--popup-y-padding) + 4px);
  margin: calc(var(--popup-y-padding) / 2) 0px 0px;
  font-weight: bold;
  font-size: 18px;
  border: none;
  background-color: var(--background-color);
`;

const editorHeightSpec = `
  height: 100%;
  min-height: 10vh;
  max-height: 20vh;
  @media ${mobileMediaQuery} {
    min-height: 40vh;
    max-height: 50vh;
  }
`;

const EditorBody = styled(CardBody)`
  background-color: var(--background-color);
  padding: var(--popup-y-padding) var(--popup-x-padding);

  ${(props) =>
    !props.$viewmode
      ? 'padding: 0px;'
      : `${editorHeightSpec}; display: block;`};
  overflow-y: auto;

  > textarea {
    background-color: var(--background-color);
    display: block;
    box-sizing: border-box;
    width: 100%;
    font-size: 1em;
    padding: var(--popup-y-padding) var(--popup-x-padding);
    border: none;

    ${editorHeightSpec}
  }
`;

const fieldTypes = {
  TITLE: 'title',
  BODY: 'body',
};

const defaultEditState = {
  title: false,
  body: false,
};

const editModeReducer = (state, action) => {
  switch (action.field) {
    case 'title': {
      return { title: true, body: false };
    }
    case 'body': {
      return { title: false, body: true };
    }
    case 'both': {
      return { title: true, body: true };
    }
    default: {
      return defaultEditState;
    }
  }
};

const NoteEditor = ({
  note,
  anchorName,
  onAnchorNameClick,
  readonly,
  close,
  saveNote,
  deleteNote,
}) => {
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(
    false,
  );

  const {
    mapState: { confirmNoteModalOpen, setConfirmNoteModalOpen },
  } = useContext(MapContext);

  const { setMobilePanelExpanded } = useContext(MobilePanelContext);

  const [editState, dispatchEditState] = useReducer(
    editModeReducer,
    defaultEditState,
  );

  const getOnClickField = (e, field) => {
    if (e.target.nodeName === 'A') {
      e.stopImmediatePropagation();
    } else {
      dispatchEditState({ field });
    }
  };

  const resetEditState = () => {
    dispatchEditState({ field: null });
  };

  const saveAndClose = async (values) => {
    const result = await saveNote({
      ...note,
      ...values,
    });
    captureEvent('note-save', { anchorType: note.anchorType });

    if (result) close();
  };

  const deleteAndClose = async () => {
    const result = await deleteNote(note);
    captureEvent('note-delete', { anchorType: note.anchorType });

    if (result) close();
  };

  const onCloseClick = (dirty) =>
    dirty ? setConfirmNoteModalOpen(true) : close();

  const onDeleteClick = () => {
    setIsConfirmDeleteModalOpen(true);
  };

  useEffect(() => {
    setMobilePanelExpanded(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!note.id) {
      dispatchEditState({ field: 'both' });
    } else {
      resetEditState();
    }
  }, [note.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const titleIsViewMode = !editState.title || readonly;
  const bodyIsViewMode = !editState.body || readonly;

  return (
    <EditorContainer>
      <ConfirmationModal
        isOpen={isConfirmDeleteModalOpen}
        title="Delete note?"
        actionDescriptionSentence="This will delete your note."
        actionName="Delete"
        onCancel={() => {
          setIsConfirmDeleteModalOpen(false);
        }}
        onConfirm={() => {
          deleteAndClose();
          setIsConfirmDeleteModalOpen(false);
        }}
      />
      <Formik
        enableReinitialize
        initialValues={{
          title: note.title || '',
          body: note.body || '',
        }}
        onSubmit={saveAndClose}
      >
        <Form>
          <EditorHeader>
            <div className="d-flex flex-row">
              <CardSubtitle
                tag={Button}
                color="text"
                className="p-0"
                size="sm"
                onClick={onAnchorNameClick}
              >
                {anchorName}
              </CardSubtitle>
            </div>
            <EditorTitle tag="h3" $viewmode={titleIsViewMode}>
              {titleIsViewMode ? (
                <LinkifiedTextField
                  onClick={(e) => getOnClickField(e, fieldTypes.TITLE)}
                  value={note?.title}
                  type={fieldTypes.TITLE}
                  defaultValue="Untitled"
                />
              ) : (
                <EditorTitleInput
                  name="title"
                  placeholder="Untitled"
                  tag={Field}
                  autoFocus
                />
              )}
            </EditorTitle>
          </EditorHeader>
          <EditorBody
            $viewmode={bodyIsViewMode}
            onClick={(e) => getOnClickField(e, fieldTypes.BODY)}
          >
            {bodyIsViewMode ? (
              <LinkifiedTextField
                onClick={(e) => getOnClickField(e, fieldTypes.BODY)}
                value={note?.body}
                type={fieldTypes.BODY}
              />
            ) : (
              <Field
                name="body"
                placeholder="Write your note here..."
                as="textarea"
                onInput={(e) => resizeTextarea(e.target)}
                onFocus={(e) => resizeTextarea(e.target)}
                autoFocus={titleIsViewMode} // Only autofocus body if title is not in edit mode
              />
            )}
          </EditorBody>
          <NoteSaveConfirmation
            isOpen={confirmNoteModalOpen}
            onCancel={() => {
              setConfirmNoteModalOpen(false);
              close();
            }}
            onConfirm={async (values) => {
              await saveAndClose(values);
              setConfirmNoteModalOpen(false);
            }}
          />
          <NoteEditorToolbar
            readonly={readonly}
            onDeleteClick={onDeleteClick}
            onCloseClick={onCloseClick}
            onCloseViewerClick={close}
            isNewNote={!note.id}
          />
        </Form>
      </Formik>
    </EditorContainer>
  );
};

export default NoteEditor;
